import { Component, ChangeDetectionStrategy, OnInit, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TitleCasePipe, CommonModule } from '@angular/common';
import * as canvasConfetti from 'canvas-confetti';
import { ButtonComponent } from 'src/app/shared/components/button/button.component';

@Component({
  selector: 'app-welcome-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ButtonComponent],
  providers: [TitleCasePipe],
  templateUrl: './welcome-page.component.html',
  styleUrls: ['./welcome-page.component.scss'],
})
export class WelcomePageComponent implements OnInit {
  private readonly titleCasePipe = inject(TitleCasePipe);
  protected readonly router = inject(Router);

  protected showSuccess = signal(true);
  protected showInstructions = signal(false);
  protected clubName = signal<string>('');
  protected adminEmail = signal<string>('');
  protected coachEmail = signal<string>('');
  protected coachName = signal<string>('');
  protected emailDetails = signal({ subject: '', body: '' });

  protected readonly adminAppsteps = [
    { title: 'Download the ActivityPro Admin App and log in using your Admin Email and Password.', icon: 'fas fa-download', content: 'admin-app-links' },
    { title: 'Set up Stripe to enable payments from club members. In the admin app, go to Menu → Setup → Stripe to set up Stripe.', icon: 'fab fa-stripe-s' },
    { title: 'Add venues and coaches to your team as needed.', icon: 'fas fa-map-marked-alt' },
    { title: 'Schedule and organize your coaching sessions.', icon: 'fas fa-calendar-alt' },
  ];

  protected readonly memberAppsteps = [
    { title: 'Encourage members to download the ActivityBuddy app.', icon: 'fas fa-users', content: 'member-app-links' },
    { title: 'Locate your club in ActivityBuddy and follow the instructions.', icon: 'fas fa-compass' },
    { title: 'Send an email to your members with the ActivityBuddy download link.', icon: 'fas fa-at', content: 'shareable-app-links' },
  ];

  ngOnInit(): void {
    const name = sessionStorage.getItem('clubName');
    const club = this.titleCasePipe.transform(name) || 'Your Club';
    this.clubName.set(club);
    this.adminEmail.set(sessionStorage.getItem('onboarding_admin_email') || '');
    this.coachEmail.set(sessionStorage.getItem('onboarding_coach_email') || '');
    this.coachName.set(sessionStorage.getItem('onboarding_coach_name') || '');

    this.emailDetails.set({
      subject: encodeURIComponent(`Join Our ${club} Club on ActivityBuddy!`),
      body: encodeURIComponent(`Dear Club Members,\n\nWe're thrilled to announce that we've launched our brand new club app – ActivityBuddy!\n\nDownload ActivityBuddy today:\n\n- Apple: https://apps.apple.com/in/app/activitybuddy/id6612029343\n- Android: https://play.google.com/store/apps/details?id=app.activitypro.activitybuddy\n\nBest regards,\n${club}`)
    });

    setTimeout(() => this.triggerConfetti(), 500);
    setTimeout(() => this.showInstructions.set(true), 1500);
  }

  protected openUrl(url: string): void {
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  protected copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      // Brief visual feedback could be added here
    });
  }

  private triggerConfetti(): void {
    const confetti: any = canvasConfetti;
    const fire = confetti.default || confetti;
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 25, spread: 360, ticks: 60, zIndex: 9999 };
    const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;
    const interval: any = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) return clearInterval(interval);
      const particleCount = 20 * (timeLeft / duration);
      fire({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
      fire({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
    }, 400);
  }
}
